/**
 * SignaturePad — schlankes Canvas-basiertes Signatur-Widget ohne externe Library.
 *
 * Props:
 *   value:    aktuell gespeicherte Signatur als data:image/png;base64,... (oder leer)
 *   onChange: callback(base64) — wird beim Loslassen (mouseup/touchend) aufgerufen
 *   label:    Beschriftung über dem Pad (z.B. "Bauleiter")
 *   testId:   data-testid Präfix
 *   height:   Höhe in px (default 140)
 *
 * Bedienung: Maus / Finger / Stylus zeichnen. Button "Löschen" leert.
 */
import { useRef, useEffect, useState, useCallback } from "react";
import { Eraser, Pen } from "lucide-react";

export default function SignaturePad({
  value = "",
  onChange,
  label = "Unterschrift",
  testId = "signature-pad",
  height = 140,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  // isEmpty als Ref (kein State) damit User-Strich isEmpty=false setzt ohne Re-Render
  const isEmptyRef = useRef(!value);
  const [isEmpty, setIsEmptyVisual] = useState(!value);

  // Canvas zeichnen + bei Größenänderung neu skalieren
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    // Weißer Hintergrund — sonst sieht man die Unterschrift im Dark-Theme nicht
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = "#0b1220";
    ctx.lineWidth = 2.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    // Bestehende Signatur laden (falls value gesetzt)
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = value;
    }
  }, [value]);

  useEffect(() => {
    setupCanvas();
    const handler = () => setupCanvas();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [setupCanvas]);

  const getPointer = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const handleStart = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPointer(e);
    isEmptyRef.current = false;
    setIsEmptyVisual(false);
  };

  const handleMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPointer(e);
    const last = lastPointRef.current || p;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
  };

  const handleEnd = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    // Signatur als PNG (data URL) ausgeben
    try {
      const data = canvasRef.current.toDataURL("image/png");
      onChange?.(data);
    } catch {
      /* noop */
    }
  };

  const clearPad = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Weißer Hintergrund nach Clear wiederherstellen
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    isEmptyRef.current = true;
    setIsEmptyVisual(true);
    onChange?.("");
  };

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-gray-200 flex items-center gap-1">
          <Pen className="w-3.5 h-3.5 opacity-70" />
          {label}
        </span>
        <button
          type="button"
          onClick={clearPad}
          className="text-[11px] text-gray-400 hover:text-red-400 inline-flex items-center gap-1"
          data-testid={`${testId}-clear`}
        >
          <Eraser className="w-3 h-3" /> Löschen
        </button>
      </div>
      <div
        className="rounded-md border-2 border-gray-400 overflow-hidden relative shadow-sm"
        style={{ height, backgroundColor: "#ffffff" }}
      >
        <canvas
          ref={canvasRef}
          className="w-full h-full block touch-none cursor-crosshair"
          style={{ backgroundColor: "#ffffff" }}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
          data-testid={`${testId}-canvas`}
        />
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs pointer-events-none select-none">
            Hier unterschreiben
          </div>
        )}
      </div>
    </div>
  );
}
